import type { ReactNode } from 'react'
import { Lock } from 'lucide-react'
import { useCan, useSession } from '../useSession'

/**
 * Gates a route behind a capability.
 *
 * Says no rather than redirecting. A teacher who follows a link to an admin
 * page needs to understand that the page exists and is not theirs — bouncing
 * them to the calendar reads as the link being broken, and the next thing that
 * happens is a message to Tonya asking why.
 *
 * Convenience only, exactly like {@link RequireSession}. The server decides
 * (D-73); every route behind this is independently capability-checked.
 */
export default function RequireCapability({
  capability,
  children,
}: {
  capability: string
  children: ReactNode
}) {
  const { isLoading } = useSession()
  const allowed = useCan(capability)

  if (isLoading) return null

  if (!allowed) {
    return (
      <div className="mx-auto w-full max-w-[34rem] px-5 py-16 text-center">
        <div className="mx-auto grid size-11 place-items-center rounded-xl bg-surface-2 text-ink-3">
          <Lock size={19} strokeWidth={2} />
        </div>
        <h1 className="mt-4 font-display text-xl">Not your page</h1>
        <p className="mt-2 text-[13.5px] text-ink-3">
          This one is for studio admins. If you need it, ask Tonya to grant you access.
        </p>
      </div>
    )
  }

  return <>{children}</>
}
